import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Heading,
  useDisclosure,
  Slide,
  CloseButton,
  Flex,
  Center,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { getTenantInfo } from '../services/api';
import AuthScreen from './AuthScreen';
import SignUpScreen from './SignUpScreen';
import OssLicensesModal from './OssLicensesModal';
import TermsModal from './TermsModal';

const LandingPage = () => {
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isOssModalOpen, setOssModalOpen] = useState(false);
  const [isTermsModalOpen, setTermsModalOpen] = useState(false);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const info = await getTenantInfo();
        setTenantInfo(info);
      } catch (err) {
        setError('会社情報の取得に失敗しました。');
        console.error('Error fetching tenant info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantInfo();
  }, []);

  const openAuthPane = (mode) => {
    setAuthMode(mode);
    onOpen();
  };

  if (loading) {
    return (
      <Center h="100vh">
        <VStack>
          <Spinner size="xl" />
          <Text>読み込み中...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100vh">
        <Alert status="error" maxW="md">
          <AlertIcon />
          {error}
        </Alert>
      </Center>
    );
  }

  return (
    <Box h="100vh" w="100%" position="relative">
      {/* ヘッダー */}
      <Box position="absolute" top={0} left={0} right={0} zIndex={20}>
        <Flex justify="space-between" align="center" p={4} bg="rgba(255, 255, 255, 0.95)" backdropFilter="blur(10px)">
          <Box>
            {tenantInfo?.logo_url && (
              <img 
                src={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071'}/api/tenant/logo`}
                alt="logo" 
                style={{ maxHeight: '40px', maxWidth: '200px' }}
              />
            )}
          </Box>
          <HStack spacing={3}>
            <Button
              variant="outline"
              bg="white"
              color="black"
              borderColor="black"
              size="sm"
              onClick={() => openAuthPane('login')}
              _hover={{
                bg: 'black',
                color: 'white',
              }}
            >
              ログイン
            </Button>
            <Button
              bg="black"
              color="white"
              size="sm"
              onClick={() => openAuthPane('signup')}
              _hover={{
                bg: 'gray.700',
              }}
            >
              新規登録
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* メインコンテンツ */}
      <Box h="100%" pt="72px" w="100vw">
        {tenantInfo?.company_intro_url ? (
          <iframe
            src={tenantInfo.company_intro_url}
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 'none' }}
            title="会社紹介"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        ) : (
          <Center h="100%">
            <VStack spacing={6}>
              <Heading size="lg">{tenantInfo?.display_name || tenantInfo?.name}</Heading>
              <Text fontSize="lg" color="gray.600">
                住宅設備・間取り選択システム
              </Text>
              <Text>
                会社紹介コンテンツが設定されていません。
              </Text>
              <HStack spacing={4}>
                <Button
                  bg="black"
                  color="white"
                  size="lg"
                  onClick={() => openAuthPane('login')}
                  _hover={{
                    bg: 'gray.700',
                  }}
                >
                  ログインして始める
                </Button>
                <Button
                  variant="outline"
                  bg="white"
                  color="black"
                  borderColor="black"
                  size="lg"
                  onClick={() => openAuthPane('signup')}
                  _hover={{
                    bg: 'black',
                    color: 'white',
                  }}
                >
                  新規登録
                </Button>
              </HStack>
            </VStack>
          </Center>
        )}
      </Box>

      {/* 右側から開くAuth画面 */}
      <Slide direction="right" in={isOpen} style={{ zIndex: 1000 }}>
        <Box
          position="fixed"
          top={0}
          right={0}
          w={{ base: "100%", md: "400px" }}
          h="100vh"
          bg="white"
          shadow="xl"
          borderLeft="1px solid"
          borderColor="gray.200"
          overflowY="auto"
        >
          <Flex justify="flex-end" p={4}>
            <CloseButton onClick={onClose} />
          </Flex>
          
          <Box px={6} pb={6}>
            {authMode === 'login' ? (
              <AuthScreen 
                onClose={onClose} 
                onSwitchToSignup={() => setAuthMode('signup')}
                isInPane={true}
              />
            ) : (
              <SignUpScreen 
                onClose={onClose} 
                onSwitchToLogin={() => setAuthMode('login')}
                isInPane={true}
              />
            )}
          </Box>
        </Box>
      </Slide>

      <Text mt={8} fontSize="xs" color="gray.400" textAlign="center">
        <Button variant="link" fontSize="xs" color="gray.400" p={0} h="auto" minW="auto" onClick={() => setTermsModalOpen(true)}>
          利用規約
        </Button>
        　|　
        <Button variant="link" fontSize="xs" color="gray.400" p={0} h="auto" minW="auto" onClick={() => setOssModalOpen(true)}>
          OSSライセンス
        </Button>
      </Text>
      <OssLicensesModal isOpen={isOssModalOpen} onClose={() => setOssModalOpen(false)} />
      <TermsModal isOpen={isTermsModalOpen} onClose={() => setTermsModalOpen(false)} />
    </Box>
  );
};

export default LandingPage;